/**
 * Flow Optimizer Drawer
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useEffect, useMemo, useState } from 'react';
import type { FlowOptimizerConfig } from '@trafficpulse/shared';

import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
} from '@/components/ui/layout/Drawer';
import { LoadingSpinner } from '@/components/ui/feedback/LoadingSpinner';

import PackagePickerBar from '@/features/flow-optimizer-library/picker/PackagePickerBar';
import { usePackageSaveFlow } from '@/features/flow-optimizer-library/save/package/use-package-save-flow';

import { ModeMasterToggle } from './components';
import DrawerStatusSlot from '@/features/flow-optimizer/drawer-shell/DrawerStatusSlot';
import FlowOptimizerDrawerFooterBar from './FlowOptimizerDrawerFooterBar';
import { FlowOptimizerDrawerFooterProvider } from './FlowOptimizerDrawerFooterContext';
import { useToast } from '@/providers/ToastProvider';
import { readHttpErrorMessage } from '@/features/flow-optimizer/tabs/standard/v2/paths/path-items/lander-editor/read-http-error';
import FlowOptimizerDrawerHeader from './FlowOptimizerDrawerHeader';
import FlowOptimizerDrawerTabs from './FlowOptimizerDrawerTabs';
import { useFlowOptimizerConfig, useSetKillSwitch, useUpsertFlowOptimizerConfig } from './hooks';
import { useDirtyCloseGuard } from './use-dirty-close-guard';
import { buildDefaultConfig } from './utils/default-config';
import { capacityRulesIssue, weightRulesIssue } from '@trafficpulse/shared';
import { validateFormulaWeights } from './tabs/calculation/weight-validation';
import type { FlowOptimizerDrawerProps } from './types/flow-optimizer.types';

const FlowOptimizerDrawer: FC<FlowOptimizerDrawerProps> = (props) => (
  <FlowOptimizerDrawerFooterProvider>
    <FlowOptimizerDrawerInner {...props} />
  </FlowOptimizerDrawerFooterProvider>
);

const FlowOptimizerDrawerInner: FC<FlowOptimizerDrawerProps> = ({
  campaignId,
  flowId,
  provider = 'voluum',
  open,
  onOpenChange,
  campaignName,
  flowName,
}) => {
  const configQuery = useFlowOptimizerConfig(campaignId, flowId, provider);
  const upsertMutation = useUpsertFlowOptimizerConfig(campaignId, provider);
  const killSwitchMutation = useSetKillSwitch(campaignId, provider);
  const toast = useToast();
  const fallback = useMemo(
    () => buildDefaultConfig(campaignId, flowId),
    [campaignId, flowId],
  );
  const [draft, setDraft] = useState<FlowOptimizerConfig>(fallback);
  // Unsaved CUSTOM config edits (mode toggle, formula groups, exclusion
  // rules) live only in `draft`; compare against the persisted config so
  // Esc/close prompts instead of silently discarding them.
  const persisted = configQuery.data ?? fallback;
  // Serialize each side independently: `persisted` only changes when the query
  // refetches, so this keeps a keystroke (which only mutates `draft`) from
  // re-stringifying the whole persisted config on every render too.
  const persistedJson = useMemo(() => JSON.stringify(persisted), [persisted]);
  const draftJson = useMemo(() => JSON.stringify(draft), [draft]);
  const configDirty = draftJson !== persistedJson;
  const guardedOpenChange = useDirtyCloseGuard(
    onOpenChange,
    configDirty && !upsertMutation.isPending,
  );

  useEffect(() => {
    if (configQuery.data) setDraft(configQuery.data);
    else if (open) setDraft(fallback);
  }, [configQuery.data, fallback, open]);

  const handleModeChange = (mode: FlowOptimizerConfig['mode']) =>
    setDraft((d) => ({ ...d, mode }));

  // Manual formula weights must each total exactly 100% per layer / sub-formula
  // group before the CUSTOM config can be saved (the backend re-normalizes, so
  // this is a UX gate). Block the Save button + give a reason while off.
  const weightIssues = useMemo(
    () => (draft.mode === 'CUSTOM' ? validateFormulaWeights(draft.formula) : []),
    [draft.mode, draft.formula],
  );
  // A capacity rule whose floor is above its ceiling deadlocks the pool for
  // good, so it is refused here as well as by the API — the red line under the
  // field was only ever advice, and advice is not a guard.
  const capacityIssue = useMemo(
    () => (draft.mode === 'CUSTOM' ? capacityRulesIssue(draft.capacityRules) : null),
    [draft.mode, draft.capacityRules],
  );
  // A weight rule without valid numbers would save as a rule that cannot run —
  // refused here with the SAME shared check the API enforces.
  const weightRuleIssue = useMemo(
    () => (draft.mode === 'CUSTOM' ? weightRulesIssue(draft.excludeRules) : null),
    [draft.mode, draft.excludeRules],
  );
  const saveBlocked = capacityIssue
    ? { title: 'Capacity limits conflict', detail: capacityIssue }
    : weightRuleIssue
      ? { title: 'Weight rule incomplete', detail: weightRuleIssue }
      : weightIssues.length > 0
        ? {
            title: 'Weights must total 100%',
            detail: `Weight totals must equal 100% — ${weightIssues
              .map((i) => `${i.label} is ${i.total}%`)
              .join(', ')}`,
          }
        : undefined;
  const saveBlockedReason = saveBlocked?.detail;

  const doSave = (packageId: string | null, packageApplied = false) => {
    if (packageId !== (draft.packageId ?? null)) {
      setDraft((d) => ({ ...d, packageId }));
    }
    upsertMutation.mutate(
      {
        flowId,
        ...(packageApplied ? { packageApplied: true } : {}),
        mode: draft.mode,
        enabled: draft.enabled,
        intervalMin: draft.intervalMin,
        minWeight: draft.minWeight,
        maxWeight: draft.maxWeight,
        standardJson: draft.standardJson,
        // Attribution rides with the draft (a loaded package sets it); the
        // backend treats an absent field as untouched, null as unassign.
        packageId,
        excludeRules: draft.excludeRules,
        formula: draft.formula,
        capacityRules: draft.capacityRules ?? [],
      },
      {
        onSuccess: () =>
          toast.success('Configuration saved', 'Flow Optimizer config updated.'),
        onError: (err) => toast.error('Save failed', readHttpErrorMessage(err)),
      },
    );
  };

  // Editing an APPLIED package asks where the changes go (everyone / this
  // campaign only / a new package) before anything is written.
  const packageSave = usePackageSaveFlow({ persisted, draft, save: doSave });

  const handleSave = () => {
    if (saveBlocked) {
      toast.error(saveBlocked.title, saveBlocked.detail);
      return;
    }
    if (packageSave.intercept()) return;
    doSave(draft.packageId ?? null);
  };

  const handleKillSwitchToggle = (next: boolean) => {
    killSwitchMutation.mutate(next, {
      onSuccess: (data) => setDraft(data),
      onError: () => {
        toast.error('Could not update push pause state. Save config first, then try again.');
      },
    });
  };

  return (
    <Drawer open={open} onOpenChange={guardedOpenChange}>
      <DrawerContent
        variant="center"
        width="w-[min(1600px,calc(100vw-2rem))]"
        className="max-h-[min(94vh,1400px)]"
      >
        <FlowOptimizerDrawerHeader
          flowId={flowId}
          campaignId={campaignId}
          campaignName={campaignName}
          flowName={flowName}
          provider={provider}
          config={draft}
        />
        <DrawerBody className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden px-4 py-0 pt-2">
          {configQuery.isLoading ? (
            <div className="flex flex-1 justify-center py-8">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <FlowOptimizerDrawerTabs
              campaignId={campaignId}
              provider={provider}
              draft={draft}
              onChange={setDraft}
              statusSlot={
                <DrawerStatusSlot
                  killSwitch={draft.killSwitch}
                  provider={provider}
                  onToggle={handleKillSwitchToggle}
                  disabled={killSwitchMutation.isPending}
                />
              }
              modeSlot={
                <ModeMasterToggle
                  mode={draft.mode}
                  onChange={handleModeChange}
                  provider={provider}
                  hideLabel
                />
              }
              librarySlot={
                <PackagePickerBar
                  draft={draft}
                  onChange={setDraft}
                  provider={provider}
                />
              }
            />
          )}
        </DrawerBody>
        <DrawerFooter className="block w-full px-4 py-3">
          <FlowOptimizerDrawerFooterBar
            flowId={flowId}
            provider={provider}
            onClose={() => guardedOpenChange(false)}
            onSaveConfig={handleSave}
            savePending={upsertMutation.isPending}
            showSaveConfig={draft.mode === 'CUSTOM'}
            saveBlockedReason={saveBlockedReason}
          />
        </DrawerFooter>
        {packageSave.dialog}
      </DrawerContent>
    </Drawer>
  );
};

export default FlowOptimizerDrawer;
